import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from './Dashboard';
import { API } from '../App';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Mail,
  MessageSquare,
  Edit,
  X,
  Save,
  CreditCard,
  DollarSign,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const Reservations = () => {
  const [reservations, setReservations] = useState([]);
  const [activities, setActivities] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [accommodationTypes, setAccommodationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [expandedPayments, setExpandedPayments] = useState({});
  const [newPayment, setNewPayment] = useState({
    amount: '',
    currency: 'DT',
    payment_method: 'cash',
    date: new Date().toISOString().split('T')[0],
    note: ''
  });
  const [newReservation, setNewReservation] = useState({
    guest_name: '',
    check_in_date: '',
    check_out_date: '',
    accommodation_type: '',
    accommodation_price: 0,
    accommodation_currency: 'EUR',
    payment_status: 'not_paid',
    payment_method: 'cash',
    guest_email: '',
    notes: '',
    establishment_id: '',
    establishment_name: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [resRes, actRes, estRes, accRes] = await Promise.all([
        axios.get(`${API}/reservations`),
        axios.get(`${API}/activities`),
        axios.get(`${API}/establishments`),
        axios.get(`${API}/accommodation-types`)
      ]);
      setReservations(resRes.data);
      setActivities(actRes.data);
      setEstablishments(estRes.data);
      setAccommodationTypes(accRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const createReservation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reservations`, newReservation);
      toast.success('Reservation created!');
      setShowCreateModal(false);
      setNewReservation({
        guest_name: '',
        check_in_date: '',
        check_out_date: '',
        accommodation_type: '',
        accommodation_price: 0,
        accommodation_currency: 'EUR',
        payment_status: 'not_paid',
        payment_method: 'cash',
        guest_email: '',
        notes: '',
        establishment_id: '',
        establishment_name: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create reservation');
    }
  };

  const updateReservation = async (id, field, value) => {
    try {
      await axios.patch(`${API}/reservations/${id}`, { [field]: value });
      loadData();
    } catch (error) {
      toast.error('Failed to update reservation');
    }
  };

  const deleteReservation = async (id) => {
    if (!window.confirm('Delete this reservation?')) return;
    try {
      await axios.delete(`${API}/reservations/${id}`);
      toast.success('Reservation deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete reservation');
    }
  };

  const addActivity = async (reservationId, activityId) => {
    try {
      await axios.post(`${API}/reservations/${reservationId}/activities`, {
        activity_id: activityId
      });
      toast.success('Activity added!');
      loadData();
    } catch (error) {
      toast.error('Failed to add activity');
    }
  };

  const removeActivity = async (reservationId, activityId) => {
    try {
      await axios.delete(`${API}/reservations/${reservationId}/activities/${activityId}`);
      toast.success('Activity removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove activity');
    }
  };

  // Payment functions
  const addPayment = async (reservationId) => {
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    const amount = parseFloat(newPayment.amount);
    const rates = { EUR: 3.3, USD: 2.5, DT: 1.0 };
    const amount_dt = amount * rates[newPayment.currency];
    
    try {
      await axios.post(`${API}/reservations/${reservationId}/payments`, {
        amount: amount,
        currency: newPayment.currency,
        amount_dt: amount_dt,
        payment_method: newPayment.payment_method,
        date: newPayment.date,
        note: newPayment.note || ''
      });
      toast.success('Payment added!');
      setNewPayment({
        amount: '',
        currency: 'DT',
        payment_method: 'cash',
        date: new Date().toISOString().split('T')[0],
        note: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to add payment');
    }
  };

  const deletePayment = async (reservationId, paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await axios.delete(`${API}/reservations/${reservationId}/payments/${paymentId}`);
      toast.success('Payment deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const togglePayments = (reservationId) => {
    setExpandedPayments(prev => ({
      ...prev,
      [reservationId]: !prev[reservationId]
    }));
  };

  const sendInvoice = async (id) => {
    try {
      const response = await axios.post(`${API}/reservations/${id}/send-invoice`);
      toast.success(response.data.message || 'Invoice sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const sendFeedback = async (id) => {
    try {
      const response = await axios.post(`${API}/checkout/${id}`);
      toast.success(response.data.message || 'Feedback email sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send feedback');
    }
  };

  const getActivityBadgeColor = (type) => {
    switch (type) {
      case 'service': return 'bg-blue-100 text-blue-800';
      case 'activity': return 'bg-green-100 text-green-800';
      case 'trip': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentMethodBadge = (method) => {
    const badges = {
      cash: { bg: 'bg-green-100', text: 'text-green-800', label: 'Cash' },
      online: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Online' },
      card: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Card' },
      transfer: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Transfer' },
      booking: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Booking.com' },
      airbnb: { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Airbnb' },
      d17: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'D17' }
    };
    return badges[method] || { bg: 'bg-gray-100', text: 'text-gray-800', label: method };
  };

  const calculateSubtotals = (reservation) => {
    const accommodationDt = reservation.accommodation_price * (
      reservation.accommodation_currency === 'EUR' ? 3.3 :
      reservation.accommodation_currency === 'USD' ? 2.5 : 1
    );
    
    const activitiesDt = reservation.activities.reduce((sum, act) => sum + act.price_dt, 0);
    
    return { accommodationDt, activitiesDt };
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-3xl font-bold text-gray-900">Reservations</h2>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-4 h-4" />
            <span>New Reservation</span>
          </button>
        </div>

        <div className="space-y-4">
          {reservations.map((reservation) => {
            const { accommodationDt, activitiesDt } = calculateSubtotals(reservation);
            const isEditing = editingReservation === reservation.id;
            const isPaymentsExpanded = expandedPayments[reservation.id];

            return (
              <div key={reservation.id} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  {/* Guest Name */}
                  <div>
                    <label className="text-xs text-gray-500">Guest Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={reservation.guest_name}
                        onChange={(e) => {
                          const updated = reservations.map(r =>
                            r.id === reservation.id ? { ...r, guest_name: e.target.value } : r
                          );
                          setReservations(updated);
                        }}
                        className="w-full px-2 py-1 border rounded"
                      />
                    ) : (
                      <p className="text-gray-900 font-semibold">{reservation.guest_name}</p>
                    )}
                    {reservation.establishment_name && (
                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded mt-1 inline-block">{reservation.establishment_name}</span>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Check-in</label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={reservation.check_in_date}
                        onChange={(e) => {
                          const updated = reservations.map(r =>
                            r.id === reservation.id ? { ...r, check_in_date: e.target.value } : r
                          );
                          setReservations(updated);
                        }}
                        className="w-full px-2 py-1 border rounded"
                      />
                    ) : (
                      <p className="text-gray-900">{reservation.check_in_date}</p>
                    )}
                  </div>

                  {/* Check-out */}
                  <div>
                    <label className="text-xs text-gray-500">Check-out</label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={reservation.check_out_date}
                        onChange={(e) => {
                          const updated = reservations.map(r =>
                            r.id === reservation.id ? { ...r, check_out_date: e.target.value } : r
                          );
                          setReservations(updated);
                        }}
                        className="w-full px-2 py-1 border rounded"
                      />
                    ) : (
                      <p className="text-gray-900">{reservation.check_out_date}</p>
                    )}
                  </div>

                  {/* Accommodation Type */}
                  <div>
                    <label className="text-xs text-gray-500">Accommodation</label>
                    {isEditing ? (
                      <select
                        value={reservation.accommodation_type}
                        onChange={(e) => {
                          const updated = reservations.map(r =>
                            r.id === reservation.id ? { ...r, accommodation_type: e.target.value } : r
                          );
                          setReservations(updated);
                        }}
                        className="w-full px-2 py-1 border rounded"
                      >
                        {accommodationTypes.map((type) => (
                          <option key={type.id} value={type.name}>{type.name}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-gray-900">{reservation.accommodation_type}</p>
                    )}
                  </div>

                  {/* Price */}
                  <div>
                    <label className="text-xs text-gray-500">Price</label>
                    {isEditing ? (
                      <div className="flex space-x-1">
                        <input
                          type="number"
                          value={reservation.accommodation_price}
                          onChange={(e) => {
                            const updated = reservations.map(r =>
                              r.id === reservation.id ? { ...r, accommodation_price: parseFloat(e.target.value) || 0 } : r
                            );
                            setReservations(updated);
                          }}
                          className="w-20 px-2 py-1 border rounded"
                        />
                        <select
                          value={reservation.accommodation_currency}
                          onChange={(e) => {
                            const updated = reservations.map(r =>
                              r.id === reservation.id ? { ...r, accommodation_currency: e.target.value } : r
                            );
                            setReservations(updated);
                          }}
                          className="px-2 py-1 border rounded"
                        >
                          <option value="EUR">EUR</option>
                          <option value="USD">USD</option>
                          <option value="DT">DT</option>
                        </select>
                      </div>
                    ) : (
                      <p className="text-gray-900 font-semibold">
                        {reservation.accommodation_price} {reservation.accommodation_currency}
                      </p>
                    )}
                  </div>

                  {/* Payment Status - now auto-calculated */}
                  <div>
                    <label className="text-xs text-gray-500">Payment Status</label>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      reservation.payment_status === 'paid'
                        ? 'bg-green-100 text-green-800'
                        : reservation.payment_status === 'partial'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {reservation.payment_status === 'paid' ? 'Paid' : 
                       reservation.payment_status === 'partial' ? 'Partial' : 'Not Paid'}
                    </span>
                  </div>

                  {/* Amount Left */}
                  <div>
                    <label className="text-xs text-gray-500">Amount Left</label>
                    <p className={`font-semibold ${reservation.amount_left_to_pay > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {(reservation.amount_left_to_pay || 0).toFixed(2)} DT
                    </p>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-xs text-gray-500">Email</label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={reservation.guest_email || ''}
                        onChange={(e) => {
                          const updated = reservations.map(r =>
                            r.id === reservation.id ? { ...r, guest_email: e.target.value } : r
                          );
                          setReservations(updated);
                        }}
                        className="w-full px-2 py-1 border rounded"
                      />
                    ) : (
                      <p className="text-gray-900 text-sm">{reservation.guest_email || 'N/A'}</p>
                    )}
                  </div>
                </div>

                {/* Activities */}
                <div className="mb-4">
                  <label className="text-xs text-gray-500 block mb-2">Activities & Services</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {reservation.activities.map((act) => (
                      <div
                        key={act.activity_id}
                        className={`flex items-center space-x-2 px-3 py-1 rounded-full ${getActivityBadgeColor(act.type)}`}
                      >
                        <span className="text-sm font-medium">{act.activity_name}</span>
                        <span className="text-xs">{act.price} {act.currency}</span>
                        <button
                          onClick={() => removeActivity(reservation.id, act.activity_id)}
                          className="hover:bg-red-200 rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        addActivity(reservation.id, e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="w-64 px-3 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="">+ Add activity</option>
                    {activities.map((act) => (
                      <option key={act.id} value={act.id}>
                        {act.name} - {act.price} {act.currency} ({act.type})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Payments Section */}
                <div className="mb-4">
                  <button
                    onClick={() => togglePayments(reservation.id)}
                    className="flex items-center space-x-2 text-sm font-semibold text-blue-600 hover:text-blue-800 mb-2"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Payments ({reservation.payments?.length || 0})</span>
                    {isPaymentsExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  
                  {isPaymentsExpanded && (
                    <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                      {/* Existing Payments List */}
                      {reservation.payments && reservation.payments.length > 0 ? (
                        <div className="space-y-2">
                          {reservation.payments.map((payment) => {
                            const badge = getPaymentMethodBadge(payment.payment_method);
                            return (
                              <div key={payment.id} className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm">
                                <div className="flex items-center space-x-4">
                                  <div>
                                    <span className="font-semibold text-gray-900">
                                      {payment.amount} {payment.currency}
                                    </span>
                                    <span className="text-gray-500 text-sm ml-2">
                                      ({payment.amount_dt.toFixed(2)} DT)
                                    </span>
                                  </div>
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${badge.bg} ${badge.text}`}>
                                    {badge.label}
                                  </span>
                                  <span className="text-gray-500 text-sm">{payment.date}</span>
                                  {payment.note && (
                                    <span className="text-gray-400 text-sm italic">&ldquo;{payment.note}&rdquo;</span>
                                  )}
                                </div>
                                <button
                                  onClick={() => deletePayment(reservation.id, payment.id)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">No payments recorded yet</p>
                      )}
                      
                      {/* Add New Payment Form */}
                      <div className="border-t pt-3 mt-3">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Add New Payment</p>
                        <div className="flex flex-wrap gap-2 items-end">
                          <div>
                            <label className="text-xs text-gray-500">Amount</label>
                            <input
                              type="number"
                              placeholder="Amount"
                              value={newPayment.amount}
                              onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                              className="w-24 px-2 py-1 border rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Currency</label>
                            <select
                              value={newPayment.currency}
                              onChange={(e) => setNewPayment({ ...newPayment, currency: e.target.value })}
                              className="px-2 py-1 border rounded text-sm"
                            >
                              <option value="DT">DT</option>
                              <option value="EUR">EUR</option>
                              <option value="USD">USD</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Method</label>
                            <select
                              value={newPayment.payment_method}
                              onChange={(e) => setNewPayment({ ...newPayment, payment_method: e.target.value })}
                              className="px-2 py-1 border rounded text-sm"
                            >
                              <option value="cash">Cash</option>
                              <option value="online">Online</option>
                              <option value="card">Card</option>
                              <option value="transfer">Transfer</option>
                              <option value="booking">Booking.com</option>
                              <option value="airbnb">Airbnb</option>
                              <option value="d17">D17</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Date</label>
                            <input
                              type="date"
                              value={newPayment.date}
                              onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                              className="px-2 py-1 border rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Note (optional)</label>
                            <input
                              type="text"
                              placeholder="Note"
                              value={newPayment.note}
                              onChange={(e) => setNewPayment({ ...newPayment, note: e.target.value })}
                              className="w-32 px-2 py-1 border rounded text-sm"
                            />
                          </div>
                          <button
                            onClick={() => addPayment(reservation.id)}
                            className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                          >
                            <Plus className="w-4 h-4" />
                            <span>Add Payment</span>
                          </button>
                        </div>
                      </div>
                      
                      {/* Payment Summary */}
                      <div className="border-t pt-3 mt-3 flex justify-between items-center">
                        <div className="text-sm">
                          <span className="text-gray-600">Total Paid: </span>
                          <span className="font-semibold text-green-600">{(reservation.total_paid_dt || 0).toFixed(2)} DT</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-600">Remaining: </span>
                          <span className={`font-semibold ${(reservation.amount_left_to_pay || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {(reservation.amount_left_to_pay || 0).toFixed(2)} DT
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Subtotals & Total */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Accommodation Subtotal:</span>
                    <span className="font-semibold text-gray-900">
                      {accommodationDt.toFixed(2)} DT
                      <span className="text-xs text-gray-500 ml-2">
                        ({reservation.accommodation_price} {reservation.accommodation_currency})
                      </span>
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Activities & Services Subtotal:</span>
                    <span className="font-semibold text-gray-900">{activitiesDt.toFixed(2)} DT</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-bold text-gray-900">Total:</span>
                    <span className="font-bold text-blue-600 text-lg">
                      {reservation.total_price_dt.toFixed(2)} DT
                    </span>
                  </div>
                </div>

                {/* Notes */}
                <div className="mb-4">
                  <label className="text-xs text-gray-500">Notes</label>
                  {isEditing ? (
                    <textarea
                      value={reservation.notes || ''}
                      onChange={(e) => {
                        const updated = reservations.map(r =>
                          r.id === reservation.id ? { ...r, notes: e.target.value } : r
                        );
                        setReservations(updated);
                      }}
                      className="w-full px-3 py-2 border rounded"
                      rows="2"
                    />
                  ) : (
                    <p className="text-gray-700 text-sm">{reservation.notes || 'No notes'}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={async () => {
                          await updateReservation(reservation.id, 'guest_name', reservation.guest_name);
                          await updateReservation(reservation.id, 'check_in_date', reservation.check_in_date);
                          await updateReservation(reservation.id, 'check_out_date', reservation.check_out_date);
                          await updateReservation(reservation.id, 'accommodation_type', reservation.accommodation_type);
                          await updateReservation(reservation.id, 'accommodation_price', reservation.accommodation_price);
                          await updateReservation(reservation.id, 'accommodation_currency', reservation.accommodation_currency);
                          await updateReservation(reservation.id, 'guest_email', reservation.guest_email);
                          await updateReservation(reservation.id, 'notes', reservation.notes);
                          setEditingReservation(null);
                          toast.success('Reservation updated!');
                        }}
                        className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                      >
                        <Save className="w-4 h-4" />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={() => {
                          setEditingReservation(null);
                          loadData();
                        }}
                        className="flex items-center space-x-1 bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                      >
                        <X className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setEditingReservation(reservation.id)}
                        className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => sendInvoice(reservation.id)}
                        className="flex items-center space-x-1 bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                      >
                        <Mail className="w-4 h-4" />
                        <span>Send Invoice</span>
                      </button>
                      <button
                        onClick={() => sendFeedback(reservation.id)}
                        className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span>Send Feedback</span>
                      </button>
                      <button
                        onClick={() => deleteReservation(reservation.id)}
                        className="flex items-center space-x-1 bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold mb-4">New Reservation</h3>
              <form onSubmit={createReservation} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Establishment *</label>
                    <select
                      required
                      value={newReservation.establishment_id}
                      onChange={(e) => {
                        const est = establishments.find(x => x.id === e.target.value);
                        setNewReservation({ ...newReservation, establishment_id: e.target.value, establishment_name: est?.name || '' });
                      }}
                      className="w-full px-3 py-2 border rounded"
                      data-testid="reservation-establishment-select"
                    >
                      <option value="">Select Establishment</option>
                      {establishments.map((est) => (
                        <option key={est.id} value={est.id}>{est.name}</option>
                      ))}
                    </select>
                  </div>
                  <input
                    type="text"
                    placeholder="Guest Name"
                    required
                    value={newReservation.guest_name}
                    onChange={(e) => setNewReservation({ ...newReservation, guest_name: e.target.value })}
                    className="px-3 py-2 border rounded"
                  />
                  <input
                    type="date"
                    required
                    value={newReservation.check_in_date}
                    onChange={(e) => setNewReservation({ ...newReservation, check_in_date: e.target.value })}
                    className="px-3 py-2 border rounded"
                  />
                  <input
                    type="date"
                    required
                    value={newReservation.check_out_date}
                    onChange={(e) => setNewReservation({ ...newReservation, check_out_date: e.target.value })}
                    className="px-3 py-2 border rounded"
                  />
                  <select
                    required
                    value={newReservation.accommodation_type}
                    onChange={(e) => setNewReservation({ ...newReservation, accommodation_type: e.target.value })}
                    className="px-3 py-2 border rounded"
                  >
                    <option value="">Select Type</option>
                    {accommodationTypes.map((type) => (
                      <option key={type.id} value={type.name}>{type.name}</option>
                    ))}
                  </select>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      placeholder="Price"
                      required
                      value={newReservation.accommodation_price}
                      onChange={(e) => setNewReservation({ ...newReservation, accommodation_price: parseFloat(e.target.value) })}
                      className="flex-1 px-3 py-2 border rounded"
                    />
                    <select
                      value={newReservation.accommodation_currency}
                      onChange={(e) => setNewReservation({ ...newReservation, accommodation_currency: e.target.value })}
                      className="px-3 py-2 border rounded"
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="DT">DT</option>
                    </select>
                  </div>
                  <input
                    type="email"
                    placeholder="Guest Email"
                    value={newReservation.guest_email}
                    onChange={(e) => setNewReservation({ ...newReservation, guest_email: e.target.value })}
                    className="px-3 py-2 border rounded"
                  />
                </div>
                <textarea
                  placeholder="Notes"
                  value={newReservation.notes}
                  onChange={(e) => setNewReservation({ ...newReservation, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="3"
                />
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                  >
                    Create Reservation
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 bg-gray-600 text-white py-2 rounded hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Reservations;
